import { useEffect, useState, type FC } from 'react';

interface HeroVideoProps {
  className?: string;
}

type IdleWindow = Window &
  typeof globalThis & {
    requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

const HeroVideo: FC<HeroVideoProps> = ({ className = '' }) => {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const smallScreen = window.matchMedia('(max-width: 767px)').matches;

    // The video is decorative. Keep it completely off the critical path on
    // mobile and for visitors who prefer reduced motion.
    if (reducedMotion || smallScreen) return;

    const idleWindow = window as IdleWindow;
    let timeoutId: number | undefined;
    let idleId: number | undefined;

    const enableVideo = () => setShouldLoad(true);

    if (idleWindow.requestIdleCallback) {
      idleId = idleWindow.requestIdleCallback(enableVideo, { timeout: 2000 });
    } else {
      timeoutId = window.setTimeout(enableVideo, 1200);
    }

    return () => {
      if (idleId !== undefined) idleWindow.cancelIdleCallback?.(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  if (!shouldLoad) return null;

  return (
    <video
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20 ${className}`}
    >
      <source src="/assets/hero-bg.mp4" type="video/mp4" />
    </video>
  );
};

export default HeroVideo;
